import {
    CountMetric,
    LatencyMetric,
    EnvironmentDetail,
    globalEventBus,
    UnsupportedEnvironmentException, VoiceNotEnabledException, InvalidInputException, WakeWordTimeoutException,
    PromiseUtils,
} from '@amzn/ihm-associate-tools-js-lib'
import * as peppermintServiceCore from '@services/voice/PeppermintServiceCore'
import { SpeechRecognitionService } from '@services/voice/SpeechRecognitionService'
import _ from 'lodash'

export const PeppermintServiceConstants = Object.freeze({
    CONFIRMATION_PROMPT_FORMAT: 'You said {0}, right?',
    PAUSE_INTENT_PROMPT: 'Sure, I will pause. Say the wake word when you are ready to answer',
    DEFAULT_RETRY_PROMPT: 'Sorry, can you please repeat that?',
    DEFAULT_CONFIRMATION_RETRIES: 2,
    CONFIRMATION_RESULT_YES: 'Yes',
    CONFIRMATION_RESULT_NO: 'No',
})

export const PeppermintServiceMethods = Object.freeze({
    GET_POSITIVE_INTEGER: 'GET_POSITIVE_INTEGER',
    GET_NUMBER: 'GET_NUMBER',
    GET_ALPHANUMERIC: 'GET_ALPHANUMERIC',
    GET_CONFIRMATION: 'GET_CONFIRMATION',
    GET_DATE: 'GET_DATE',
})

export const PeppermintServiceMethodConfigs = Object.freeze({
    GET_POSITIVE_INTEGER: Object.freeze({
        speechRecognitionFunctionName: 'getPositiveInteger',
        retryPrompt: 'I could not understand. Please say a positive number',
    }),
    GET_NUMBER: Object.freeze({
        speechRecognitionFunctionName: 'getNumber',
        retryPrompt: 'I could not understand. Please say a number',
    }),
    GET_ALPHANUMERIC: Object.freeze({
        speechRecognitionFunctionName: 'getAlphanumeric',
        retryPrompt: 'I could not understand. Please try again',
    }),
    GET_CONFIRMATION: Object.freeze({
        speechRecognitionFunctionName: 'getConfirmation',
        retryPrompt: 'I could not understand. Please say yes or no',
    }),
    GET_DATE: Object.freeze({
        speechRecognitionFunctionName: 'getDate',
        retryPrompt: 'I could not understand. Please say a specific date',
    }),
})

/**
 * This is a noop function that simply returns that data that it receives (untouched). This function is used by data types
 * that don't need any special format conversion to have {@see speechSynthesisService} say it to the user (i.e. numbers).
 * @param {string} data - string to be converted so that {@see speechSynthesisService} can say it.
 * @return {any} the data untouched.
 */
const convertResultToSpeechSynthesisNoop = (data) => {
    return data
}

/**
 * Returns a promise and works to get a confirmation (Yes/No) guided by the config argument.
 * @param {object} config - The configuration object. (see Configs in the service's doc).
 * @return {Promise} Resolves with a {@see ResponseObject} from {@see speechRecognitionService}.
 */
export const getConfirmation = (config) => {
    const fnConfig = PeppermintServiceMethodConfigs[PeppermintServiceMethods.GET_CONFIRMATION]
    return buildAndHandlePeppermintRequest({
        requestConfig: config,
        speechRecognitionFn: SpeechRecognitionService.getConfirmation,
        retryPrompt: fnConfig.retryPrompt,
        speechRecognitionFunctionName: fnConfig.speechRecognitionFunctionName,
    })
}

export const programName = 'peppermintService'
export const PEPPERMINT_DEFAULT_CONFIG = {
    convertResultToSpeechSynthesisFn: convertResultToSpeechSynthesisNoop,
    convertAlternativeBotResultToSpeechSynthesisFn: convertResultToSpeechSynthesisNoop,
    getConfirmationFn: getConfirmation,
}

export const warmup = () => {
    return peppermintServiceCore.warmup()
}

/**
 * Returns a promise and works to get a positive integer guided by the config argument.
 * @param {object} config - The configuration object. (see Configs in the service's doc).
 * @return {Promise} Resolves with a {@see ResponseObject} from {@see speechRecognitionService}.
 */
export const getPositiveInteger = (config) => {
    const fnConfig = PeppermintServiceMethodConfigs[PeppermintServiceMethods.GET_POSITIVE_INTEGER]
    return buildAndHandlePeppermintRequest({
        requestConfig: config,
        speechRecognitionFn: SpeechRecognitionService.getPositiveInteger,
        retryPrompt: fnConfig.retryPrompt,
        speechRecognitionFunctionName: fnConfig.speechRecognitionFunctionName,
    })
}

/**
 * Returns a promise and works to get a number guided by the config argument.
 * @param {object} config - The configuration object. (see Configs in the service's doc).
 * @return {Promise} Resolves with a {@see ResponseObject} from {@see speechRecognitionService}.
 *
 * Call config Example: https://code.amazon.com/packages/SWIFTOutboundPickTool/blobs/8f3a5cda7fe5ee25d9d2388c649c63cbfa6a1035/--/src/app/services/voiceService.js#L219
 * {
 *       prompt: prompt,
 *       confirmResponse: overrides.confirmResponse,             e.g default false
 *       retries: overrides.retries,                             e.g. default 4
 *       waitForWakeWord: overrides.waitForWakeWord,             e.g. true
 *       speechRecognitionConfig: overrides.speechRecognitionConfig,
 *     }
 */
export const getNumber = (config) => {
    const fnConfig = PeppermintServiceMethodConfigs[PeppermintServiceMethods.GET_NUMBER]
    return buildAndHandlePeppermintRequest({
        requestConfig: config,
        speechRecognitionFn: SpeechRecognitionService.getNumber,
        retryPrompt: fnConfig.retryPrompt,
        speechRecognitionFunctionName: fnConfig.speechRecognitionFunctionName,
    })
}

/**
 * Returns a promise and works to get an alphanumeric string guided by the config argument.
 * @param {object} config - The configuration object. (see Configs in the service's doc).
 * @return {Promise} Resolves with a {@see ResponseObject} from {@see speechRecognitionService}.
 */
export const getAlphanumeric = (config) => {
    const fnConfig = PeppermintServiceMethodConfigs[PeppermintServiceMethods.GET_ALPHANUMERIC]
    return buildAndHandlePeppermintRequest({
        requestConfig: config,
        speechRecognitionFn: SpeechRecognitionService.getAlphanumeric,
        convertResultToSpeechSynthesisFn: convertResultToSpeechSynthesisAlphanumeric,
        retryPrompt: fnConfig.retryPrompt,
        speechRecognitionFunctionName: fnConfig.speechRecognitionFunctionName,
    })
}

/**
 * Returns a promise and works to get a date (format yyyy-mm-dd) guided by the config argument.
 * @param {object} config - The configuration object. (see Configs in the service's doc).
 * @return {Promise} Resolves with a {@see ResponseObject} from {@see speechRecognitionService}.
 */
export const getDate = (config) => {
    const fnConfig = PeppermintServiceMethodConfigs[PeppermintServiceMethods.GET_DATE]
    return buildAndHandlePeppermintRequest({
        requestConfig: config,
        speechRecognitionFn: SpeechRecognitionService.getDate,
        retryPrompt: fnConfig.retryPrompt,
        speechRecognitionFunctionName: fnConfig.speechRecognitionFunctionName,
    })
}

/**
 * Returns a promise and works to get a customDataType guided by the config argument.
 * @param {object} config - The configuration object. (see Configs in the service's doc). Also must include a key
 *  customDataTypeConfig that contains an object with the dataType key. Example
 *      {
 *          prompt: ...,
 *          customDataTypeConfig: {
 *              dataType: 'PrimeNow_GetPickZone',
 *              retryPrompt: 'Please say a valid pick zone'
 *          },
 *          speechRecognitionConfig: {
 *              recordTimeInMillis: 5000
 *          }
 *      }
 * @return {Promise} Resolves with a {@see ResponseObject} from {@see speechRecognitionService}.
 */
export const getCustomDataType = (config) => {
    return handleCustomDataRequest(config)
}

/**
 * Builds a config object to call {@see peppermintServiceCore} with and performs the call.
 * @param {object} overrides - The config overrides to be applied in {@see buildPeppermintConfig}.
 * @return {Promise} promise returned by the {@see peppermintServiceCore.handleCustomDataRequest}.
 */
const buildAndHandlePeppermintRequest = (overrides) => {
    const peppermintRequestConfig = buildPeppermintConfig(overrides)
    return peppermintServiceCore.handlePeppermintRequest(peppermintRequestConfig)
}

/**
 * Builds a config that can be used to call {@see peppermintServiceCore}. Bases this config on the {@see PEPPERMINT_DEFAULT_CONFIG}
 * and accepts overrides for it.
 * @param {object} override - Object to override the {@see PEPPERMINT_DEFAULT_CONFIG}.
 * @return {object} A config object that can be used to call {@see peppermintServiceCore}.
 */
const buildPeppermintConfig = (overrides) => {
    const defaultConfig = getPeppermintDefaultConfig()
    return Object.assign(defaultConfig, overrides)
}

/**
 * Creates a copy of the {@see PEPPERMINT_DEFAULT_CONFIG}. We copy it so that it can be modified without altering the
 * original.
 * @return {object} the default config.
 */
const getPeppermintDefaultConfig = () => {
    return PEPPERMINT_DEFAULT_CONFIG
}

/**
 * Validates the customDataRequestConfig and adapts the call to mimic a normal peppermintRequest. Then, calls peppermintRequest.
 *
 * @param {object} customDataRequestConfig - The configuration object for customDataType.
 * @return {Promise} Same promise as {@see handlePeppermintRequest} if config is valid, InvalidInputException if it's invalid.
 */
const handleCustomDataRequest = (customDataRequestConfig) => {
    // We only use this metric to count fault as 0 if we fail validation here. handlePeppermintRequest will emit the 1 count if there
    // is a fault.
    const faultMetric = new CountMetric(undefined, 'Fault', programName, 'peppermintService.getCustomDataType')
    if (!customDataRequestConfig) {
        faultMetric.count(0)
        return Promise.reject(new InvalidInputException({
            message: 'config is required',
        }))
    }
    if (!customDataRequestConfig.customDataTypeConfig) {
        faultMetric.count(0)
        return Promise.reject(new InvalidInputException({
            message: 'config.customDataTypeConfig is required',
        }))
    }
    if (!customDataRequestConfig.customDataTypeConfig.dataType) {
        faultMetric.count(0)
        return Promise.reject(new InvalidInputException({
            message: 'config.customDataTypeConfig.dataType is required',
        }))
    }
    const dataType = customDataRequestConfig.customDataTypeConfig.dataType
    const speechRecognitionOpts = {
        dataType,
    }

    const retryPrompt = customDataRequestConfig.customDataTypeConfig.retryPrompt || PeppermintServiceConstants.DEFAULT_RETRY_PROMPT

    return buildAndHandlePeppermintRequest({
        requestConfig: customDataRequestConfig,
        speechRecognitionFn: customDataTypeSpeechRecongitionFn,
        convertResultToSpeechSynthesisFn: convertResultToSpeechSynthesisNoop,
        speechRecognitionFunctionName: 'getCustomDataType',
        retryPrompt,
        statsMetricName: dataType,
    })

    /**
     * Merges the configurations that we need to pass to the speechRecognitionService.getCustomDataType function with the speechRecognitionConfig
     * provided by the caller.
     *
     * This is the function that will be called when we need to do speech recogition for this custom data type. We define this function
     * in this closure to be able to use the current context.
     */
    function customDataTypeSpeechRecongitionFn (opts) {
        const requestOpts = Object.assign(_.cloneDeep(speechRecognitionOpts), opts)
        return SpeechRecognitionService.getCustomDataType(requestOpts)
    }
}

/**
 * Converts an alphanumeric string into something that {@see speechSynthesisService} can say with proper semantics. For example,
 * AAA will be read as A. A. A. instead of as a single word.
 * @param {string} data - string to be converted so that {@see speechSynthesisService} can say it.
 * @return {string} the converted data.
 */
const convertResultToSpeechSynthesisAlphanumeric = (data) => {
    return data.split('').join('. ') + '.'

}
